// -----------------------------------------------------------------------------
// Floating chat pop-out, anchored to the bottom-right corner of every
// authenticated route except /chat itself (the full page covers the
// same surface there).
//
// Two views inside the panel:
//   - "list":  compact channel + DM picker, ordered by recency, with
//              unread badges and a search box.
//   - "conv":  active conversation - header with back button + "open
//              full chat" shortcut, the shared `MessageList` bubble UI,
//              and the same `Composer` the chat page uses.
//
// Selection, "is open?", and last viewed channel are persisted in
// session/localStorage so the popup remembers where you were across
// page navigations and reloads. Hides itself entirely on /chat to
// avoid running two SignalR fan-outs side-by-side and double-counting
// the read state.
// -----------------------------------------------------------------------------

import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  ArrowLeftIcon,
  ArrowTopRightOnSquareIcon,
  BellSlashIcon,
  ChatBubbleLeftRightIcon,
  HashtagIcon,
  MagnifyingGlassIcon,
  PencilSquareIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import classNames from "../../utils/classNames";
import { channelLabel, fullNameFor } from "../../utils/chatHelpers";
import { useAuth } from "../../contexts/AuthContext";
import { useSignalR } from "../../contexts/SignalRContext";
import useChat, { chatQueryKeys } from "../../data/useChat";
import { ACTIVE_CHANNEL_STORAGE_KEY } from "../../hooks/useChatDesktopNotifications";
import { IChatChannel, IChatMessage, IChatUser } from "../../models/Chat";
import ChatAvatar from "../../pages/Chat/ChatAvatar";
import { formatRelative } from "../../pages/Chat/chatTime";
import MessageList from "../../pages/Chat/MessageList";
import Composer from "../../pages/Chat/Composer";
import NewChannelModal from "../../pages/Chat/modals/NewChannelModal";
import NewDmModal from "../../pages/Chat/modals/NewDmModal";

const POPUP_OPEN_KEY = "chat.popup.open";
const POPUP_CHANNEL_KEY = "chat.popup.lastChannelId";

export default function ChatPopup() {
  const location = useLocation();
  const { loggedIn, loading } = useAuth();

  const onChatPage =
    location.pathname === "/chat" || location.pathname.startsWith("/chat/");

  // Don't even mount when we shouldn't render. This avoids spinning up
  // duplicate SignalR subscriptions / mark-read writes alongside the
  // full chat page, and also stops the popup from briefly rendering
  // before auth is ready.
  if (loading || !loggedIn || onChatPage) return null;

  return <ChatPopupInner />;
}

function ChatPopupInner() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const auth = useAuth();
  const { chatConnection, chatPresence } = useSignalR();

  const meId = auth.loggedIn ? auth.userId : null;

  const chat = useChat();
  const usersQuery = chat.users.data?.data ?? [];
  const channelsQuery = chat.channels.data?.data ?? [];

  const users: IChatUser[] = useMemo(
    () =>
      usersQuery.map((u) => ({
        ...u,
        presence: chatPresence[u.id] ?? u.presence ?? "offline",
        isMe: u.id === meId,
      })),
    [usersQuery, chatPresence, meId]
  );
  const me = users.find((u) => u.isMe) ?? null;
  const usersById = useMemo(() => {
    const map = new Map<string, IChatUser>();
    users.forEach((u) => map.set(u.id, u));
    return map;
  }, [users]);

  const sortedChannels = useMemo(
    () =>
      [...channelsQuery].sort((a, b) => {
        const aTime = a.lastMessageAt
          ? Date.parse(a.lastMessageAt)
          : Date.parse(a.createdAt);
        const bTime = b.lastMessageAt
          ? Date.parse(b.lastMessageAt)
          : Date.parse(b.createdAt);
        return bTime - aTime;
      }),
    [channelsQuery]
  );

  const totalUnread = useMemo(
    () =>
      sortedChannels
        .filter((c) => !c.muted)
        .reduce((sum, c) => sum + (c.unreadCount ?? 0), 0),
    [sortedChannels]
  );

  // Open / close state - persisted so the user's preference survives
  // a hard reload. Defaults to closed so we never surprise the user
  // with an interrupting modal on first sign-in.
  const [open, setOpen] = useState<boolean>(() => {
    try {
      return localStorage.getItem(POPUP_OPEN_KEY) === "true";
    } catch {
      return false;
    }
  });
  useEffect(() => {
    try {
      localStorage.setItem(POPUP_OPEN_KEY, open ? "true" : "false");
    } catch {
      /* storage disabled - in-memory state is enough */
    }
  }, [open]);

  // Selected channel - sessionStorage so two tabs don't fight for the
  // same value. Resolves to the actual channel object below; we hold
  // the id so it survives a refetch that swaps array references.
  const [selectedId, setSelectedId] = useState<string | null>(() => {
    try {
      return sessionStorage.getItem(POPUP_CHANNEL_KEY);
    } catch {
      return null;
    }
  });
  useEffect(() => {
    try {
      if (selectedId) {
        sessionStorage.setItem(POPUP_CHANNEL_KEY, selectedId);
      } else {
        sessionStorage.removeItem(POPUP_CHANNEL_KEY);
      }
    } catch {
      /* fine */
    }
  }, [selectedId]);

  const selected = useMemo(
    () => channelsQuery.find((c) => c.id === selectedId) ?? null,
    [channelsQuery, selectedId]
  );

  // The desktop-notifications hook reads this key to decide whether to
  // suppress alerts for the channel the user is actively reading.
  // Update it whenever the popup is open AND a channel is selected,
  // mirroring what the full chat page does for its own selection.
  useEffect(() => {
    if (open && selectedId) {
      sessionStorage.setItem(ACTIVE_CHANNEL_STORAGE_KEY, selectedId);
    }
    return () => {
      // Clear on unmount or when the popup closes; the chat page (when
      // we navigate to it) will rewrite the value on its own.
      const current = sessionStorage.getItem(ACTIVE_CHANNEL_STORAGE_KEY);
      if (current === selectedId) {
        sessionStorage.removeItem(ACTIVE_CHANNEL_STORAGE_KEY);
      }
    };
  }, [open, selectedId]);

  const messagesQuery = chat.useMessages(open ? selectedId : null);
  const messages = messagesQuery.data?.data ?? [];

  // Identify so the SignalR hub joins us into our channel groups -
  // the chat page does the same on mount; doing it here too means the
  // popup stays live for users that never visit /chat.
  useEffect(() => {
    if (!chatConnection || !meId) return;
    chatConnection.invoke("Identify", meId).catch((err) => {
      console.warn("[chat-popup] identify failed", err);
    });
  }, [chatConnection, meId]);

  // SignalR fan-out -> React Query invalidations. Mirrors the full
  // chat page's wiring so live messages and reactions land in the
  // popup's UI without manual refetches.
  useEffect(() => {
    if (!chatConnection) return;

    const onMessage = (message: IChatMessage) => {
      queryClient.invalidateQueries(chatQueryKeys.messages(message.channelId));
      queryClient.invalidateQueries(chatQueryKeys.channels);
    };
    const onMessageEdited = (message: IChatMessage) => {
      queryClient.invalidateQueries(chatQueryKeys.messages(message.channelId));
    };
    const onReaction = (message: IChatMessage) => {
      queryClient.invalidateQueries(chatQueryKeys.messages(message.channelId));
    };
    const onChannel = () => {
      queryClient.invalidateQueries(chatQueryKeys.channels);
    };
    const onChannelRemoved = (channelId: string) => {
      queryClient.setQueryData(chatQueryKeys.channels, (old: any) => {
        if (!old?.data) return old;
        return {
          ...old,
          data: old.data.filter((c: IChatChannel) => c.id !== channelId),
        };
      });
      queryClient.removeQueries(chatQueryKeys.messages(channelId));
      setSelectedId((current) => (current === channelId ? null : current));
    };

    chatConnection.on("MessageReceived", onMessage);
    chatConnection.on("MessageEdited", onMessageEdited);
    chatConnection.on("ReactionUpdated", onReaction);
    chatConnection.on("ChannelCreated", onChannel);
    chatConnection.on("ChannelUpdated", onChannel);
    chatConnection.on("ChannelRemoved", onChannelRemoved);

    return () => {
      chatConnection.off("MessageReceived", onMessage);
      chatConnection.off("MessageEdited", onMessageEdited);
      chatConnection.off("ReactionUpdated", onReaction);
      chatConnection.off("ChannelCreated", onChannel);
      chatConnection.off("ChannelUpdated", onChannel);
      chatConnection.off("ChannelRemoved", onChannelRemoved);
    };
  }, [chatConnection, queryClient]);

  // Mark-read while the popup is open and showing the channel.
  // Defers slightly to avoid thrashing during the initial render.
  const markReadMutation = chat.markRead;
  useEffect(() => {
    if (!open || !selected) return;
    const handle = window.setTimeout(() => {
      markReadMutation.mutate(selected.id);
    }, 250);
    return () => window.clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selected?.id, messages.length]);

  // Search across both lists so the popup stays single-pane.
  const [search, setSearch] = useState("");
  const filteredChannels = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return sortedChannels;
    return sortedChannels.filter((c) => {
      const label = channelLabel(c, me?.id ?? null, usersById).toLowerCase();
      const desc = (c.description ?? "").toLowerCase();
      return label.includes(term) || desc.includes(term);
    });
  }, [sortedChannels, search, me, usersById]);

  const teamChannels = filteredChannels.filter((c) => c.kind === "channel");
  const dmChannels = filteredChannels.filter((c) => c.kind === "dm");

  const [showNewChannel, setShowNewChannel] = useState(false);
  const [showNewDm, setShowNewDm] = useState(false);

  const handleSend = (body: string, replyToId?: string) => {
    if (!selected) return;
    chat.sendMessage.mutate({
      channelId: selected.id,
      body: { body, replyToId },
    });
  };
  const handleEdit = (messageId: string, body: string) => {
    chat.editMessage.mutate({ messageId, body });
  };
  const handleReact = (messageId: string, emoji: string) => {
    chat.toggleReaction.mutate({ messageId, emoji });
  };

  // Auto-scroll the conversation pane to the bottom whenever the
  // active channel or message count changes - same heuristic the
  // full ChannelThread uses.
  const scrollRef = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open || !selected) return;
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [open, selected?.id, messages.length]);

  const view: "list" | "conv" = selected ? "conv" : "list";

  // ---------------------------------------------------------------
  // Closed state - just the floating launcher button.
  // ---------------------------------------------------------------
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={
          totalUnread > 0
            ? `${totalUnread} unread chat message${totalUnread === 1 ? "" : "s"}`
            : "Open chat"
        }
        className="fixed bottom-5 right-5 z-30 flex h-14 w-14 items-center justify-center rounded-full bg-primary-600 text-white shadow-lg transition hover:bg-primary-500 hover:shadow-xl"
      >
        <ChatBubbleLeftRightIcon className="h-7 w-7" />
        {totalUnread > 0 ? (
          <span className="absolute -right-1 -top-1 inline-flex min-w-[22px] items-center justify-center rounded-full bg-rose-500 px-1.5 py-0.5 text-[11px] font-bold text-white shadow ring-2 ring-white">
            {totalUnread > 99 ? "99+" : totalUnread}
          </span>
        ) : null}
      </button>
    );
  }

  // ---------------------------------------------------------------
  // Open state - panel.
  // ---------------------------------------------------------------
  return (
    <>
      <div className="fixed bottom-5 right-5 z-30 flex h-[560px] max-h-[calc(100vh-7rem)] w-[380px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
        {view === "list" ? (
          <ListHeader
            totalUnread={totalUnread}
            onClose={() => setOpen(false)}
            onOpenFull={() => navigate("/chat")}
          />
        ) : (
          <ConversationHeader
            channel={selected!}
            me={me}
            usersById={usersById}
            onBack={() => setSelectedId(null)}
            onClose={() => setOpen(false)}
            onOpenFull={() =>
              navigate(`/chat?channel=${selected!.id}`)
            }
          />
        )}

        {view === "list" ? (
          <ChannelListBody
            me={me}
            usersById={usersById}
            teamChannels={teamChannels}
            dmChannels={dmChannels}
            search={search}
            onSearch={setSearch}
            onSelect={setSelectedId}
            onNewChannel={() => setShowNewChannel(true)}
            onNewDm={() => setShowNewDm(true)}
            loading={chat.channels.isLoading}
          />
        ) : null}

        {view === "conv" && selected && me ? (
          <>
            <div
              ref={scrollRef}
              className="flex-1 space-y-1 overflow-y-auto bg-gray-50/60 px-3 py-3"
            >
              {messagesQuery.isLoading && messages.length === 0 ? (
                <div className="flex h-full items-center justify-center text-sm text-gray-400">
                  Loading messages…
                </div>
              ) : (
                <MessageList
                  messages={messages}
                  usersById={usersById}
                  meId={me.id}
                  onEdit={handleEdit}
                  onReact={handleReact}
                />
              )}
            </div>
            <Composer
              channel={selected}
              usersById={usersById}
              meId={me.id}
              onSend={handleSend}
            />
          </>
        ) : null}
      </div>

      <NewChannelModal
        open={showNewChannel}
        onClose={() => setShowNewChannel(false)}
        users={users}
        meId={me?.id ?? null}
        creating={chat.createChannel.isLoading}
        onCreate={(input) => {
          chat.createChannel.mutate(input, {
            onSuccess: (res) => {
              const created = res?.data;
              if (created?.id) setSelectedId(created.id);
              setShowNewChannel(false);
            },
          });
        }}
      />
      <NewDmModal
        open={showNewDm}
        onClose={() => setShowNewDm(false)}
        users={users.filter((u) => !u.isMe)}
        starting={chat.startDm.isLoading}
        onSelect={(userId) => {
          chat.startDm.mutate(userId, {
            onSuccess: (res) => {
              const channel = res?.data;
              if (channel?.id) setSelectedId(channel.id);
              setShowNewDm(false);
            },
          });
        }}
      />
    </>
  );
}

// ---------------------------------------------------------------------------
// Header components
// ---------------------------------------------------------------------------

function ListHeader({
  totalUnread,
  onClose,
  onOpenFull,
}: {
  totalUnread: number;
  onClose: () => void;
  onOpenFull: () => void;
}) {
  return (
    <header className="flex items-center gap-2 border-b border-gray-200 bg-white px-4 py-3">
      <ChatBubbleLeftRightIcon className="h-5 w-5 text-primary-600" />
      <div className="min-w-0 flex-1">
        <p className="text-sm font-semibold text-gray-900">Chat</p>
        <p className="text-[11px] text-gray-500">
          {totalUnread > 0
            ? `${totalUnread} unread message${totalUnread === 1 ? "" : "s"}`
            : "All caught up"}
        </p>
      </div>
      <IconButton title="Open full chat" onClick={onOpenFull}>
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      </IconButton>
      <IconButton title="Close" onClick={onClose}>
        <XMarkIcon className="h-4 w-4" />
      </IconButton>
    </header>
  );
}

function ConversationHeader({
  channel,
  me,
  usersById,
  onBack,
  onClose,
  onOpenFull,
}: {
  channel: IChatChannel;
  me: IChatUser | null;
  usersById: Map<string, IChatUser>;
  onBack: () => void;
  onClose: () => void;
  onOpenFull: () => void;
}) {
  const title = channelLabel(channel, me?.id ?? null, usersById);
  const otherMembers = channel.memberIds
    .map((id) => usersById.get(id))
    .filter((u): u is IChatUser => !!u && u.id !== me?.id);

  return (
    <header className="flex items-center gap-2 border-b border-gray-200 bg-white px-3 py-2.5">
      <IconButton title="Back to channels" onClick={onBack}>
        <ArrowLeftIcon className="h-4 w-4" />
      </IconButton>

      <div className="flex min-w-0 flex-1 items-center gap-2">
        {channel.kind === "channel" ? (
          <HashtagIcon className="h-4 w-4 shrink-0 text-gray-400" />
        ) : otherMembers[0] ? (
          <ChatAvatar user={otherMembers[0]} size="xs" showPresence tooltip={false} />
        ) : null}
        <p className="truncate text-sm font-semibold text-gray-900">
          {channel.kind === "channel" ? channel.name : title}
        </p>
        {channel.muted ? (
          <BellSlashIcon className="h-3.5 w-3.5 shrink-0 text-gray-400" title="Muted" />
        ) : null}
      </div>

      <IconButton title="Open in full chat" onClick={onOpenFull}>
        <ArrowTopRightOnSquareIcon className="h-4 w-4" />
      </IconButton>
      <IconButton title="Close" onClick={onClose}>
        <XMarkIcon className="h-4 w-4" />
      </IconButton>
    </header>
  );
}

function IconButton({
  children,
  title,
  onClick,
}: {
  children: React.ReactNode;
  title: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-7 w-7 items-center justify-center rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-700"
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Channel list body (search + sections + rows)
// ---------------------------------------------------------------------------

function ChannelListBody({
  me,
  usersById,
  teamChannels,
  dmChannels,
  search,
  onSearch,
  onSelect,
  onNewChannel,
  onNewDm,
  loading,
}: {
  me: IChatUser | null;
  usersById: Map<string, IChatUser>;
  teamChannels: IChatChannel[];
  dmChannels: IChatChannel[];
  search: string;
  onSearch: (value: string) => void;
  onSelect: (id: string) => void;
  onNewChannel: () => void;
  onNewDm: () => void;
  loading: boolean;
}) {
  return (
    <div className="flex flex-1 flex-col overflow-hidden">
      <div className="border-b border-gray-100 bg-white px-3 py-2">
        <label className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1.5 text-sm shadow-sm focus-within:border-primary-500 focus-within:ring-1 focus-within:ring-primary-500">
          <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
          <input
            type="text"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search channels and people"
            className="w-full border-0 bg-transparent p-0 text-sm placeholder:text-gray-400 focus:ring-0"
          />
        </label>
      </div>

      <div className="flex-1 overflow-y-auto bg-gray-50/40 px-2 py-2">
        <PopupSection
          title="Channels"
          actionLabel="New thread"
          onAction={onNewChannel}
        >
          {loading && teamChannels.length === 0 ? (
            <li className="px-3 py-2 text-xs text-gray-400">Loading…</li>
          ) : teamChannels.length === 0 ? (
            <li className="px-3 py-2 text-xs text-gray-400">
              {search ? "No matching channels" : "No channels yet"}
            </li>
          ) : (
            teamChannels.map((c) => (
              <PopupRow
                key={c.id}
                onClick={() => onSelect(c.id)}
                leading={<HashtagIcon className="h-4 w-4 text-gray-500" />}
                title={c.name}
                subtitle={c.description}
                unread={c.unreadCount ?? 0}
                muted={!!c.muted}
                trailingText={formatRelative(c.lastMessageAt)}
              />
            ))
          )}
        </PopupSection>

        <PopupSection
          title="Direct messages"
          actionLabel="New chat"
          onAction={onNewDm}
        >
          {loading && dmChannels.length === 0 ? (
            <li className="px-3 py-2 text-xs text-gray-400">Loading…</li>
          ) : dmChannels.length === 0 ? (
            <li className="px-3 py-2 text-xs text-gray-400">
              {search ? "No matching conversations" : "No direct messages yet"}
            </li>
          ) : (
            dmChannels.map((c) => {
              const otherId = c.memberIds.find((id) => id !== me?.id);
              const other = otherId ? usersById.get(otherId) : undefined;
              return (
                <PopupRow
                  key={c.id}
                  onClick={() => onSelect(c.id)}
                  leading={
                    other ? (
                      <ChatAvatar user={other} size="xs" showPresence tooltip={false} />
                    ) : null
                  }
                  title={other ? fullNameFor(other) : "Direct message"}
                  subtitle={other?.role}
                  unread={c.unreadCount ?? 0}
                  muted={!!c.muted}
                  trailingText={formatRelative(c.lastMessageAt)}
                />
              );
            })
          )}
        </PopupSection>
      </div>
    </div>
  );
}

function PopupSection({
  title,
  actionLabel,
  onAction,
  children,
}: {
  title: string;
  actionLabel?: string;
  onAction?: () => void;
  children: React.ReactNode;
}) {
  return (
    <div className="px-1 py-1.5">
      <div className="flex items-center justify-between px-2 pb-1">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
          {title}
        </span>
        {actionLabel && onAction ? (
          <button
            type="button"
            onClick={onAction}
            className="inline-flex items-center gap-1 rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-500 shadow-sm hover:border-primary-300 hover:bg-primary-50 hover:text-primary-700"
          >
            <PencilSquareIcon className="h-3 w-3" />
            <span>{actionLabel}</span>
          </button>
        ) : null}
      </div>
      <ul className="space-y-0.5">{children}</ul>
    </div>
  );
}

function PopupRow({
  onClick,
  leading,
  title,
  subtitle,
  unread,
  muted,
  trailingText,
}: {
  onClick: () => void;
  leading: React.ReactNode;
  title: string;
  subtitle?: string;
  unread: number;
  muted?: boolean;
  trailingText?: string;
}) {
  const hasUnread = unread > 0;
  return (
    <li>
      <button
        type="button"
        onClick={onClick}
        className="group flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm text-gray-700 hover:bg-white"
      >
        <span className="flex h-5 w-5 items-center justify-center">
          {leading}
        </span>
        <span className="min-w-0 flex-1">
          <span className="flex items-center justify-between gap-2">
            <span className="flex min-w-0 items-center gap-1">
              <span
                className={classNames(
                  "truncate",
                  hasUnread && !muted ? "font-semibold text-gray-900" : "",
                  muted ? "text-gray-500" : ""
                )}
              >
                {title}
              </span>
              {muted ? (
                <BellSlashIcon
                  className="h-3.5 w-3.5 shrink-0 text-gray-400"
                  title="Muted"
                />
              ) : null}
            </span>
            {trailingText ? (
              <span className="shrink-0 text-[10px] text-gray-400">
                {trailingText}
              </span>
            ) : null}
          </span>
          {subtitle ? (
            <span className="mt-0.5 block truncate text-xs text-gray-500">
              {subtitle}
            </span>
          ) : null}
        </span>
        {hasUnread ? (
          <span
            className={classNames(
              "shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-semibold leading-none",
              muted ? "bg-gray-300 text-gray-700" : "bg-primary-600 text-white"
            )}
          >
            {unread > 99 ? "99+" : unread}
          </span>
        ) : null}
      </button>
    </li>
  );
}
